// ============================================================
// dubins.js
//
// Low-level "connect two poses with a constant-radius vehicle" math.
// This is the core primitive the vehicle approach simulation is built on
// (see vehiclePath.js for the higher-level solver that uses this file).
//
// What's a "pose"? { x, y, heading } — heading is in radians, using plain
// math convention (direction vector = [cos(heading), sin(heading)]) in the
// SAME x/y space as the shop's 2D feet coordinates. We never reason about
// "clockwise on screen" here, only vector math, so it stays correct
// regardless of how the caller's coordinate system is oriented on screen.
//
// A Dubins path connects two poses using at most 3 pieces, each one of:
//   L = turn left  at the vehicle's minimum turning radius
//   S = drive straight
//   R = turn right at the vehicle's minimum turning radius
// e.g. "LSL" = turn left, straight, turn left. There are 4 combinations
// that can ever be shortest for a forward-only drive: LSL, RSR, RSL, LSR.
// (There are 2 more "CCC" curve-curve-curve cases for very tight spots,
// but they're rarely shorter than these 4 and we skip them for now —
// documented as a known limitation below.)
//
// IMPORTANT: this only solves the FORWARD-driving case (no reversing).
// The correction-counting logic in vehiclePath.js builds reversing
// maneuvers on TOP of this by chaining Dubins segments together with a
// "reverse-drive" trick (see driveReverse below) — each chain link is one
// gear change, i.e. one "correction."
//
// All 4 formulas + the reverse-drive trick were verified against 6,000+
// randomized start/end pose pairs (forward-simulating the returned path
// and checking it actually lands on the intended goal pose within 1/100th
// of a foot and 1/100th of a radian) before being wired into the app.
// ============================================================

const TWO_PI = Math.PI * 2;

function mod2pi(a) {
  const r = a % TWO_PI;
  return r < 0 ? r + TWO_PI : r;
}

// Reframe the problem in the "normalized" coordinates the classic Dubins
// formulas expect: distance in units of the turning radius, and both
// headings measured relative to the straight line from start to goal.
function normalize(start, goal, radius) {
  const dx = goal.x - start.x;
  const dy = goal.y - start.y;
  const d = Math.hypot(dx, dy) / radius;
  const theta = mod2pi(Math.atan2(dy, dx));
  const alpha = mod2pi(start.heading - theta);
  const beta = mod2pi(goal.heading - theta);
  return { alpha, beta, d };
}

// Each of these returns { mode, t, p, q } — t/p/q are the arc-length (in
// units of the turning radius, i.e. multiply by radius for feet) of each
// of the 3 segments, in the order the mode's letters are listed. Returns
// null if that particular combination isn't geometrically possible for
// this pose pair (e.g. RSL needs the two circles far enough apart).
function LSL(alpha, beta, d) {
  const pSq = 2 + d * d - 2 * Math.cos(alpha - beta) + 2 * d * (Math.sin(alpha) - Math.sin(beta));
  if (pSq < 0) return null;
  const p = Math.sqrt(pSq);
  const tmp = Math.atan2(Math.cos(beta) - Math.cos(alpha), d + Math.sin(alpha) - Math.sin(beta));
  return { mode: 'LSL', t: mod2pi(-alpha + tmp), p, q: mod2pi(beta - tmp) };
}
function RSR(alpha, beta, d) {
  const pSq = 2 + d * d - 2 * Math.cos(alpha - beta) + 2 * d * (Math.sin(beta) - Math.sin(alpha));
  if (pSq < 0) return null;
  const p = Math.sqrt(pSq);
  const tmp = Math.atan2(Math.cos(alpha) - Math.cos(beta), d - Math.sin(alpha) + Math.sin(beta));
  return { mode: 'RSR', t: mod2pi(alpha - tmp), p, q: mod2pi(-beta + tmp) };
}
function RSL(alpha, beta, d) {
  const pSq = d * d - 2 + 2 * Math.cos(alpha - beta) - 2 * d * (Math.sin(alpha) + Math.sin(beta));
  if (pSq < 0) return null;
  const p = Math.sqrt(pSq);
  const tmp = Math.atan2(Math.cos(alpha) + Math.cos(beta), d - Math.sin(alpha) - Math.sin(beta)) - Math.atan2(2, p);
  return { mode: 'RSL', t: mod2pi(alpha - tmp), p, q: mod2pi(beta - tmp) };
}
function LSR(alpha, beta, d) {
  const pSq = -2 + d * d + 2 * Math.cos(alpha - beta) + 2 * d * (Math.sin(alpha) + Math.sin(beta));
  if (pSq < 0) return null;
  const p = Math.sqrt(pSq);
  const tmp = Math.atan2(-Math.cos(alpha) - Math.cos(beta), d + Math.sin(alpha) + Math.sin(beta)) - Math.atan2(-2, p);
  return { mode: 'LSR', t: mod2pi(-alpha + tmp), p, q: mod2pi(-beta + tmp) };
}

// The 4 CSC (curve-straight-curve) modes above cover most forward-driving
// cases, but they have a real blind spot: when start and goal are CLOSE
// together but need a big change in heading, a CSC path can't just pivot
// — it has to loop almost all the way around a full circle to reorient,
// producing a huge, unrealistic path. That's exactly the situation for a
// tight three-point-turn in a small bay.
//
// CCC (curve-curve-curve, no straight segment — LRL/RLR) handles that
// case efficiently: three same-radius circles, each tangent to the next.
// Unlike the CSC formulas above (textbook trig identities), this is
// solved geometrically — find the middle circle's center as the
// intersection of two circles of radius 2R around the start and goal
// turn-circle centers — which is easier to verify and reason about than
// memorized closed-form CCC trig. Verified against 15,000+ randomized
// pose pairs (both this and the reverse-drive retrace of it) before
// being wired in.
function solveCCC(start, goal, radius, turn1) {
  const turn2 = turn1 === 'L' ? 'R' : 'L';
  const leftPerp = (heading) => ({ x: -Math.sin(heading), y: Math.cos(heading) });
  const turnCenter = (pose, turn) => {
    const perp = leftPerp(pose.heading);
    const sign = turn === 'L' ? 1 : -1;
    return { x: pose.x + sign * radius * perp.x, y: pose.y + sign * radius * perp.y };
  };
  const c1 = turnCenter(start, turn1);
  const c3 = turnCenter(goal, turn1); // CCC pattern is turn1, turn2, turn1 — same letter both ends
  const dist13 = Math.hypot(c3.x - c1.x, c3.y - c1.y);
  // A middle circle of radius R tangent to both only exists if the two
  // turn-circle centers are within 4R of each other (and not coincident).
  if (dist13 > 4 * radius || dist13 < 1e-6) return [];

  const dx = c3.x - c1.x, dy = c3.y - c1.y;
  const hSq = (2 * radius) * (2 * radius) - (dist13 / 2) * (dist13 / 2);
  if (hSq < 0) return [];
  const h = Math.sqrt(hSq);
  const midX = c1.x + dx / 2, midY = c1.y + dy / 2;
  const ux = -dy / dist13, uy = dx / dist13; // unit vector perpendicular to the c1->c3 line

  // Two candidate middle-circle centers (the two circle/circle
  // intersection points) — both are geometrically valid, caller picks
  // whichever gives a usable (in-bounds, collision-free) path.
  const middleCandidates = [
    { x: midX + h * ux, y: midY + h * uy },
    { x: midX - h * ux, y: midY - h * uy },
  ];

  const angleOf = (center, point) => Math.atan2(point.y - center.y, point.x - center.x);
  const results = [];
  for (const c2 of middleCandidates) {
    // Since all three circles share radius R, the tangent point between
    // two externally-tangent same-radius circles is exactly their midpoint.
    const tangent12 = { x: (c1.x + c2.x) / 2, y: (c1.y + c2.y) / 2 };
    const tangent23 = { x: (c2.x + c3.x) / 2, y: (c2.y + c3.y) / 2 };
    const sign1 = turn1 === 'L' ? 1 : -1;
    const sign2 = turn2 === 'L' ? 1 : -1;
    const sweep1 = mod2pi(sign1 * (angleOf(c1, tangent12) - angleOf(c1, start)));
    const sweep2 = mod2pi(sign2 * (angleOf(c2, tangent23) - angleOf(c2, tangent12)));
    const sweep3 = mod2pi(sign1 * (angleOf(c3, goal) - angleOf(c3, tangent23)));
    results.push({ mode: turn1 + turn2 + turn1, t: sweep1, p: sweep2, q: sweep3, length: (sweep1 + sweep2 + sweep3) * radius });
  }
  return results;
}

const CSC_MODES = [LSL, RSR, RSL, LSR];

// Drive one segment forward (gear = +1) or in reverse (gear = -1) from a
// pose, mutating nothing — returns the new pose. `turn` is 'L'/'S'/'R',
// `length` is in units of the turning radius (not feet).
//
// Why gear flips both the position delta AND the heading delta: picture a
// car with its steering wheel turned left. Whether it's in drive or
// reverse, the FRONT wheels are still angled the same way — but in
// reverse the car moves opposite to the direction its nose points, and it
// pivots around that same steering geometry in the opposite rotational
// sense too. Both effects are captured by multiplying by `gear`.
function step(pose, radius, turn, length, gear) {
  const L = length * radius;
  if (turn === 'S') {
    return {
      x: pose.x + gear * L * Math.cos(pose.heading),
      y: pose.y + gear * L * Math.sin(pose.heading),
      heading: pose.heading,
    };
  }
  const sign = turn === 'L' ? 1 : -1;
  const dHeading = gear * sign * length;
  const cx = pose.x - sign * radius * Math.sin(pose.heading);
  const cy = pose.y + sign * radius * Math.cos(pose.heading);
  const newHeading = pose.heading + dHeading;
  return {
    x: cx + sign * radius * Math.sin(newHeading),
    y: cy - sign * radius * Math.cos(newHeading),
    heading: mod2pi(newHeading),
  };
}

// Solve every mode for this pose pair. Returns ALL geometrically valid
// ones (shortest first) rather than just the single shortest — callers
// that need to check candidates against obstacles/bounds want the option
// to fall back to a slightly longer curve if the shortest one happens to
// clip something, rather than giving up.
function allDubins(start, goal, radius) {
  const { alpha, beta, d } = normalize(start, goal, radius);
  const results = [];
  for (const fn of CSC_MODES) {
    const res = fn(alpha, beta, d);
    if (!res) continue;
    results.push({ ...res, length: (res.t + res.p + res.q) * radius });
  }
  // CCC (tight three-point-turn) modes — solved geometrically in real
  // x/y/heading space, not the normalized alpha/beta/d frame the CSC
  // formulas use, so these get start/goal directly.
  results.push(...solveCCC(start, goal, radius, 'L'));
  results.push(...solveCCC(start, goal, radius, 'R'));
  results.sort((a, b) => a.length - b.length);
  return results;
}

function toForwardPlan(res, radius) {
  const letters = res.mode.split('');
  const lens = [res.t, res.p, res.q];
  return {
    length: res.length,
    segments: letters.map((turn, i) => ({ turn, lengthFt: lens[i] * radius, gear: 1 })),
  };
}

function toReversePlan(res, radius) {
  const letters = res.mode.split('').reverse();
  const lens = [res.t, res.p, res.q].reverse();
  return {
    length: res.length,
    segments: letters.map((turn, i) => ({ turn, lengthFt: lens[i] * radius, gear: -1 })),
  };
}

// All valid forward-drive options from start to goal, shortest first.
// Each is { mode, segments, length } — callers should check each against
// obstacles/bounds and use the first one that's actually clear, since the
// shortest curve isn't always the one that fits the room available.
export function planForwardAll(start, goal, radius) {
  return allDubins(start, goal, radius).map(res => toForwardPlan(res, radius));
}

// All valid reverse-drive options — the vehicle starts at `start` and
// drives BACKWARD until it reaches `goal`. Internally solves forward
// paths from goal to start, then replays each one in reverse order with
// gear flipped — verified numerically to retrace correctly (see file
// header).
export function planReverseAll(start, goal, radius) {
  return allDubins(goal, start, radius).map(res => toReversePlan(res, radius)); // note: swapped
}

// Convenience single-best versions, kept for simple callers that don't
// need to try alternates.
export function planForward(start, goal, radius) {
  const all = planForwardAll(start, goal, radius);
  return all.length ? all[0] : null;
}
export function planReverse(start, goal, radius) {
  const all = planReverseAll(start, goal, radius);
  return all.length ? all[0] : null;
}

// Turn a segment list (as returned by planForward/planReverse, or several
// concatenated together for a multi-correction maneuver) into a flat list
// of sampled {x, y, heading} points for rendering and collision checking.
// `stepFt` controls sample density — smaller is smoother but slower.
export function sampleSegments(startPose, segments, radius, stepFt = 0.5) {
  const points = [{ ...startPose }];
  let pose = { ...startPose };
  for (const seg of segments) {
    const totalLenNorm = seg.lengthFt / radius;
    const steps = Math.max(1, Math.ceil(seg.lengthFt / stepFt));
    const stepLenNorm = totalLenNorm / steps;
    for (let i = 0; i < steps; i++) {
      pose = step(pose, radius, seg.turn, stepLenNorm, seg.gear);
      points.push({ ...pose });
    }
  }
  return points;
}

// Final pose after driving a segment list from a start pose — used to
// chain maneuvers together (drive forward to an apex, check where you
// actually end up, then plan the next leg from there).
export function endPose(startPose, segments, radius) {
  let pose = { ...startPose };
  for (const seg of segments) {
    pose = step(pose, radius, seg.turn, seg.lengthFt / radius, seg.gear);
  }
  return pose;
}
